import React, { Component } from "react";
import * as Icon from "react-feather";
import EngagementTrackerService from "../../services/EngagementTrackerService";
import DataMapperService from "../../services/DataMapperService";

const TYPE_OPTIONS = [
	{ key: "text", label: "Text", icon: "Type" },
	{ key: "dropdown", label: "Dropdown", icon: "ChevronDown" },
	{ key: "checkbox", label: "Checkbox", icon: "CheckSquare" },
	{ key: "date", label: "Date", icon: "Calendar" },
];

// A small palette to seed new dropdown options with.
const PALETTE = [
	"#cfe9c8", // soft green
	"#a8dca0", // green
	"#f7d0c1", // soft peach
	"#f4b6a0", // red-orange
	"#fde3a7", // soft yellow
	"#c9d8f5", // soft blue
	"#dfe3eb", // grey
	"#e8d0f4", // soft lavender
];

class EditFormModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			activeResource: "engagement",
			fields: this._cloneFields(props.fields),
			renameValue: props.formName || "",
			showRename: false,
			showDeleteConfirm: false,
			saving: false,
			dirty: false,
			// Fixed datasource entities + relationships (loaded once).
			dsEntities: [],
			dsRelationships: [],
			dsNativeFields: {}, // { resource: [{key,label,type}] } — copyable native fields
		};
		// Per-resource working copies + originals (for save diffing).
		this._cache = { engagement: this._cloneFields(props.fields) };
		this._originals = { engagement: props.fields || [] };
	}

	async componentDidMount() {
		try {
			const registry = await DataMapperService.getRegistry();
			this.setState({
				dsEntities: (registry && registry.entities) || [],
				dsRelationships: (registry && registry.relationships) || [],
				dsNativeFields: (registry && registry.nativeFields) || {},
			});
		} catch (e) {
			console.log(e);
		}
	}

	// Switch between Engagement / Tasks / Users column sets.
	onSelectResource = async resource => {
		if (resource === this.state.activeResource) return;
		// Stash current edits.
		this._cache[this.state.activeResource] = this.state.fields;
		if (this._cache[resource]) {
			this.setState({ activeResource: resource, fields: this._cache[resource] });
			return;
		}
		// Lazy-load this resource's columns once.
		this.setState({ activeResource: resource, fields: [] });
		const loaded = await EngagementTrackerService.listFields({ formName: this.props.formName, resource, formOwnerId: this.props.formOwnerId });
		this._originals[resource] = loaded;
		const cloned = this._cloneFields(loaded);
		this._cache[resource] = cloned;
		if (this.state.activeResource === resource) this.setState({ fields: cloned });
	};

	_cloneFields(fields) {
		return (fields || []).map(f => ({
			id: f.id,
			field_name: f.field_name,
			field_type: f.field_type,
			options: Array.isArray(f.options) ? f.options.map(o => ({ ...o })) : [],
			datasource: f.datasource ? { entity: f.datasource.entity, parentFieldId: f.datasource.parentFieldId || null } : null,
			__dsMode: !!(f.datasource && f.datasource.entity),
			order_index: f.order_index,
			__isNew: false,
		}));
	}

	_entityName(key) {
		const e = (this.state.dsEntities || []).find(x => x.key === key);
		return e ? e.name : key;
	}

	// Entities reachable as a child of the given parent entity (relationship target).
	_childEntitiesFor(parentEntityKey) {
		return (this.state.dsRelationships || [])
			.filter(r => r.source === parentEntityKey)
			.map(r => r.target);
	}

	// Sibling fields (already saved) usable as a datasource parent.
	_parentCandidates(currentField) {
		return this.state.fields.filter(
			f => f.id && f !== currentField && f.field_type === "dropdown" && f.datasource && f.datasource.entity
		);
	}

	// ---------- Field operations ----------

	onFieldNameChange = (idx, value) => {
		const fields = this.state.fields.slice();
		fields[idx] = { ...fields[idx], field_name: value };
		this.setState({ fields });
	};

	onFieldTypeChange = (idx, value) => {
		const fields = this.state.fields.slice();
		const next = { ...fields[idx], field_type: value };
		// Changing type invalidates a native copy-source of a different type.
		next.nativeSource = null;
		// Seed default options if switching to dropdown with no options.
		if (value === "dropdown" && (!Array.isArray(next.options) || next.options.length === 0)) {
			next.options = [
				{ key: "option_1", label: "Option 1", color: PALETTE[0] },
				{ key: "option_2", label: "Option 2", color: PALETTE[2] },
			];
		}
		fields[idx] = next;
		this.setState({ fields });
	};

	onNativeSourceChange = (idx, key) => {
		const fields = this.state.fields.slice();
		fields[idx] = { ...fields[idx], nativeSource: key || null };
		this.setState({ fields });
	};

	// Native fields of the same type as this column, for the active resource.
	_nativeSourceOptions(fieldType) {
		const list = (this.state.dsNativeFields && this.state.dsNativeFields[this.state.activeResource]) || [];
		return list.filter(n => n.type === fieldType);
	}

	onAddField = () => {
		const fields = this.state.fields.slice();
		fields.push({
			id: null,
			field_name: "New Column",
			field_type: "text",
			options: [],
			datasource: null,
			nativeSource: null,
			__dsMode: false,
			order_index: fields.length,
			__isNew: true,
		});
		this.setState({ fields });
	};

	onRemoveField = idx => {
		const fields = this.state.fields.slice();
		fields.splice(idx, 1);
		this.setState({ fields });
	};

	onMoveField = (idx, delta) => {
		const fields = this.state.fields.slice();
		const target = idx + delta;
		if (target < 0 || target >= fields.length) return;
		const tmp = fields[idx];
		fields[idx] = fields[target];
		fields[target] = tmp;
		this.setState({ fields });
	};

	// ---------- Dropdown options ----------

	onAddOption = fieldIdx => {
		const fields = this.state.fields.slice();
		const opts = fields[fieldIdx].options ? fields[fieldIdx].options.slice() : [];
		const nextNum = opts.length + 1;
		opts.push({
			key: "option_" + Date.now(),
			label: "Option " + nextNum,
			color: PALETTE[opts.length % PALETTE.length],
		});
		fields[fieldIdx] = { ...fields[fieldIdx], options: opts };
		this.setState({ fields });
	};

	_slug(s) {
		return (
			String(s || "")
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, "_")
				.replace(/^_+|_+$/g, "") || "opt"
		);
	}

	// Seed the dropdown with the company's engagement statuses (flow_states) as
	// editable manual options. These are copies — adding/editing/removing them
	// here never touches the original flow_states/flows.
	onLoadEngagementStatuses = fieldIdx => {
		const flowStates = this.props.flowStates || [];
		if (!flowStates.length) return;
		const fields = this.state.fields.slice();
		const opts = fields[fieldIdx].options ? fields[fieldIdx].options.slice() : [];
		const existingLabels = new Set(opts.map(o => (o.label || "").trim().toLowerCase()));
		const existingKeys = new Set(opts.map(o => o.key));
		const seen = new Set();
		flowStates.forEach((fs, i) => {
			const name = (fs.name || "").trim();
			if (!name) return;
			const lower = name.toLowerCase();
			// Statuses repeat across flows (each flow has its own "Not Started"); keep
			// one option per distinct name.
			if (seen.has(lower) || existingLabels.has(lower)) return;
			seen.add(lower);
			let key = this._slug(name);
			while (existingKeys.has(key)) key = key + "_" + i;
			existingKeys.add(key);
			opts.push({ key, label: name, color: fs.color || PALETTE[opts.length % PALETTE.length] });
		});
		fields[fieldIdx] = { ...fields[fieldIdx], options: opts };
		this.setState({ fields });
	};

	onChangeOption = (fieldIdx, optIdx, patch) => {
		const fields = this.state.fields.slice();
		const opts = fields[fieldIdx].options.slice();
		opts[optIdx] = { ...opts[optIdx], ...patch };
		fields[fieldIdx] = { ...fields[fieldIdx], options: opts };
		this.setState({ fields });
	};

	onRemoveOption = (fieldIdx, optIdx) => {
		const fields = this.state.fields.slice();
		const opts = fields[fieldIdx].options.slice();
		opts.splice(optIdx, 1);
		fields[fieldIdx] = { ...fields[fieldIdx], options: opts };
		this.setState({ fields });
	};

	// ---------- Datasource (entity-backed) dropdowns ----------

	onDropdownSourceChange = (idx, mode) => {
		const fields = this.state.fields.slice();
		const f = { ...fields[idx] };
		if (mode === "datasource") {
			f.__dsMode = true;
			f.options = [];
			if (!f.datasource) {
				const first = (this.state.dsEntities || [])[0];
				f.datasource = { entity: first ? first.key : "", parentFieldId: null };
			}
		} else {
			f.__dsMode = false;
			f.datasource = null;
			if (!Array.isArray(f.options) || f.options.length === 0) {
				f.options = [
					{ key: "option_1", label: "Option 1", color: PALETTE[0] },
					{ key: "option_2", label: "Option 2", color: PALETTE[2] },
				];
			}
		}
		fields[idx] = f;
		this.setState({ fields });
	};

	onDatasourceEntityChange = (idx, entityKey) => {
		const fields = this.state.fields.slice();
		const ds = { ...(fields[idx].datasource || {}), entity: entityKey || "" };
		fields[idx] = { ...fields[idx], datasource: ds };
		this.setState({ fields });
	};

	onDatasourceParentChange = (idx, parentFieldId) => {
		const fields = this.state.fields.slice();
		const ds = { ...(fields[idx].datasource || {}), parentFieldId: parentFieldId || null };
		if (parentFieldId) {
			// Constrain the entity to a valid child of the chosen parent.
			const parent = this.state.fields.find(p => String(p.id) === String(parentFieldId));
			const children = parent && parent.datasource ? this._childEntitiesFor(parent.datasource.entity) : [];
			if (children.length && children.indexOf(ds.entity) === -1) ds.entity = children[0];
		}
		fields[idx] = { ...fields[idx], datasource: ds };
		this.setState({ fields });
	};

	// ---------- Form-level ----------

	onToggleRename = () => {
		this.setState({ showRename: !this.state.showRename, renameValue: this.props.formName });
	};

	onRenameChange = e => {
		this.setState({ renameValue: e.target.value });
	};

	onConfirmRename = async () => {
		const next = (this.state.renameValue || "").trim();
		if (!next || next === this.props.formName) {
			this.setState({ showRename: false });
			return;
		}
		await EngagementTrackerService.renameForm({ formName: this.props.formName, newFormName: next, formOwnerId: this.props.formOwnerId });
		this.setState({ showRename: false });
		this.props.onClose(true);
	};

	onConfirmDeleteForm = async () => {
		await EngagementTrackerService.deleteForm({ formName: this.props.formName, formOwnerId: this.props.formOwnerId });
		this.props.onClose(true);
	};

	// ---------- Save all (the simple, robust path) ----------

	async _saveResource(resource, original, current) {
		// 1. Deletes: anything in original but not in current
		const currentIds = new Set(current.filter(f => f.id).map(f => f.id));
		for (const o of original) {
			if (!currentIds.has(o.id)) {
				// eslint-disable-next-line no-await-in-loop
				await EngagementTrackerService.deleteField({ fieldId: o.id });
			}
		}
		// 2. Creates / 3. Updates
		for (let i = 0; i < current.length; i++) {
			const f = current[i];
			const isDatasource = f.field_type === "dropdown" && f.__dsMode && f.datasource && f.datasource.entity;
			let optionsPayload = null;
			if (f.field_type === "dropdown") {
				optionsPayload = isDatasource
					? { datasource: { entity: f.datasource.entity, parentFieldId: f.datasource.parentFieldId || null } }
					: f.options;
			}
			const fieldBody = { field_name: f.field_name, field_type: f.field_type, options: optionsPayload, order_index: i };
			if (!f.id) {
				// nativeSource seeds initial values on creation only (copy snapshot).
				if (f.nativeSource) fieldBody.nativeSource = f.nativeSource;
				// eslint-disable-next-line no-await-in-loop
				await EngagementTrackerService.addField({ formName: this.props.formName, field: fieldBody, resource, formOwnerId: this.props.formOwnerId });
			} else {
				// eslint-disable-next-line no-await-in-loop
				await EngagementTrackerService.updateField({ fieldId: f.id, updates: fieldBody });
			}
		}
	}

	onSaveAll = async () => {
		this.setState({ saving: true });
		// Flush the active resource's edits into the cache before saving.
		this._cache[this.state.activeResource] = this.state.fields;

		for (const resource of Object.keys(this._cache)) {
			// eslint-disable-next-line no-await-in-loop
			await this._saveResource(resource, this._originals[resource] || [], this._cache[resource] || []);
		}

		this.setState({ saving: false });
		this.props.onClose(true);
	};

	// ---------- Datasource config sub-render ----------

	renderDatasourceConfig(f, idx) {
		const ds = f.datasource || {};
		const parents = this._parentCandidates(f);
		const parentChosen = !!ds.parentFieldId;
		const parentField = parentChosen ? this.state.fields.find(p => String(p.id) === String(ds.parentFieldId)) : null;
		// When a parent is chosen, the entity is constrained to valid children of it.
		const childEntities = parentField && parentField.datasource ? this._childEntitiesFor(parentField.datasource.entity) : [];
		const entityOptions = parentChosen
			? (this.state.dsEntities || []).filter(e => childEntities.indexOf(e.key) !== -1)
			: this.state.dsEntities || [];

		return (
			<div className="et-edit__ds">
				<label className="et-edit__ds-row">
					<span className="et-edit__ds-label">Depends on</span>
					<select
						className="et-edit__ds-select"
						value={ds.parentFieldId || ""}
						onChange={e => this.onDatasourceParentChange(idx, e.target.value)}
						disabled={parents.length === 0}
					>
						<option value="">None (show all)</option>
						{parents.map(p => (
							<option key={p.id} value={p.id}>
								{p.field_name} ({this._entityName(p.datasource.entity)})
							</option>
						))}
					</select>
				</label>

				<label className="et-edit__ds-row">
					<span className="et-edit__ds-label">Datasource</span>
					<select
						className="et-edit__ds-select"
						value={ds.entity || ""}
						onChange={e => this.onDatasourceEntityChange(idx, e.target.value)}
					>
						<option value="">Select an entity…</option>
						{entityOptions.map(e => (
							<option key={e.key} value={e.key}>
								{e.name}
							</option>
						))}
					</select>
				</label>

				{parentChosen && entityOptions.length === 0 && (
					<div className="et-edit__ds-empty">
						<Icon.AlertTriangle size={12} />
						<span>The selected parent has no related entity to populate from.</span>
					</div>
				)}
				{!parentChosen && parents.length === 0 && ds.entity && (
					<div className="et-edit__ds-hint">
						Tip: to make this depend on another column, first add &amp; save a datasource dropdown, then
						pick it under “Depends on”.
					</div>
				)}
			</div>
		);
	}

	// ---------- Render ----------

	render() {
		const { fields, renameValue, showRename, showDeleteConfirm, saving } = this.state;
		const { formName } = this.props;

		return (
			<div className="et-modal-overlay" onClick={() => this.props.onClose(false)}>
				<div className="et-modal et-modal--lg" onClick={e => e.stopPropagation()}>
					<div className="et-modal__header">
						<h2 className="et-modal__title">
							<Icon.Settings size={16} style={{ marginRight: 8 }} />
							Edit Columns
						</h2>
						<button className="et-modal__close" onClick={() => this.props.onClose(false)}>
							<Icon.X size={16} />
						</button>
					</div>

					<div className="et-modal__body">
						{/* Form name + rename + delete */}
						<div className="et-edit__form-row">
							{!showRename ? (
								<>
									<div className="et-edit__form-name">
										<Icon.Layers size={13} style={{ marginRight: 6, opacity: 0.6 }} />
										{formName}
									</div>
									<div className="et-edit__form-actions">
										<button className="et-btn et-btn--ghost et-btn--sm" onClick={this.onToggleRename}>
											<Icon.Edit2 size={12} />
											<span>Rename</span>
										</button>
										<button
											className="et-btn et-btn--danger-ghost et-btn--sm"
											onClick={() => this.setState({ showDeleteConfirm: true })}
										>
											<Icon.Trash2 size={12} />
											<span>Delete form</span>
										</button>
									</div>
								</>
							) : (
								<>
									<input
										className="et-edit__rename-input"
										autoFocus
										value={renameValue}
										onChange={this.onRenameChange}
									/>
									<div className="et-edit__form-actions">
										<button className="et-btn et-btn--ghost et-btn--sm" onClick={this.onToggleRename}>
											Cancel
										</button>
										<button className="et-btn et-btn--primary et-btn--sm" onClick={this.onConfirmRename}>
											Save name
										</button>
									</div>
								</>
							)}
						</div>

						{showDeleteConfirm && (
							<div className="et-edit__confirm-danger">
								<Icon.AlertTriangle size={14} />
								<span>
									Delete form <strong>{formName}</strong>? Columns will be archived and your saved values
									kept (but hidden).
								</span>
								<div className="et-edit__confirm-actions">
									<button
										className="et-btn et-btn--ghost et-btn--sm"
										onClick={() => this.setState({ showDeleteConfirm: false })}
									>
										Cancel
									</button>
									<button className="et-btn et-btn--danger et-btn--sm" onClick={this.onConfirmDeleteForm}>
										Delete form
									</button>
								</div>
							</div>
						)}

						{/* Resource tabs: which sub-grid's columns we're editing */}
						<div className="et-edit__restabs">
							{[
								{ key: "engagement", label: "Engagement" },
								{ key: "task", label: "Tasks" },
								{ key: "user", label: "Users" },
							].map(t => (
								<button
									key={t.key}
									className={"et-edit__restab " + (this.state.activeResource === t.key ? "et-edit__restab--on" : "")}
									onClick={() => this.onSelectResource(t.key)}
								>
									{t.label}
								</button>
							))}
						</div>

						{/* Columns */}
						<div className="et-edit__cols-title">
							<span>{this.state.activeResource === "engagement" ? "Engagement columns" : this.state.activeResource === "task" ? "Task columns" : "User columns"}</span>
							<button className="et-btn et-btn--ghost et-btn--sm" onClick={this.onAddField}>
								<Icon.Plus size={12} />
								<span>Add column</span>
							</button>
						</div>

						<div className="et-edit__cols">
							{fields.length === 0 && (
								<div className="et-edit__cols-empty">No columns yet. Add one to get started.</div>
							)}

							{fields.map((f, idx) => (
								<div className="et-edit__col" key={f.id || "new-" + idx}>
									<div className="et-edit__col-head">
										<div className="et-edit__col-handle">
											<button
												className="et-edit__col-arrow"
												disabled={idx === 0}
												onClick={() => this.onMoveField(idx, -1)}
											>
												<Icon.ChevronUp size={12} />
											</button>
											<button
												className="et-edit__col-arrow"
												disabled={idx === fields.length - 1}
												onClick={() => this.onMoveField(idx, 1)}
											>
												<Icon.ChevronDown size={12} />
											</button>
										</div>

										<input
											className="et-edit__col-name"
											value={f.field_name}
											onChange={e => this.onFieldNameChange(idx, e.target.value)}
											placeholder="Column name"
										/>

										<div className="et-edit__col-type">
											{TYPE_OPTIONS.map(t => {
												const Ico = Icon[t.icon];
												const active = f.field_type === t.key;
												return (
													<button
														key={t.key}
														className={"et-edit__type-btn " + (active ? "et-edit__type-btn--on" : "")}
														onClick={() => this.onFieldTypeChange(idx, t.key)}
														title={t.label}
													>
														{Ico && <Ico size={11} />}
														<span>{t.label}</span>
													</button>
												);
											})}
										</div>

										<button
											className="et-edit__col-remove"
											onClick={() => this.onRemoveField(idx)}
											title="Remove column"
										>
											<Icon.Trash2 size={13} />
										</button>
									</div>

									{/* Copy initial values from a matching-type native field (new columns only). */}
									{!f.id &&
										["text", "date", "checkbox"].indexOf(f.field_type) !== -1 &&
										this._nativeSourceOptions(f.field_type).length > 0 && (
											<div className="et-edit__ds">
												<label className="et-edit__ds-row">
													<span className="et-edit__ds-label">Copy from</span>
													<select
														className="et-edit__ds-select"
														value={f.nativeSource || ""}
														onChange={e => this.onNativeSourceChange(idx, e.target.value)}
													>
														<option value="">None (start empty)</option>
														{this._nativeSourceOptions(f.field_type).map(n => (
															<option key={n.key} value={n.key}>
																{n.label}
															</option>
														))}
													</select>
												</label>
												{f.nativeSource && (
													<div className="et-edit__ds-hint">
														Copies each record's value into this column once; editing it won't change the
														original field.
													</div>
												)}
											</div>
										)}

									{f.field_type === "dropdown" && (
										<div className="et-edit__opts">
											<div className="et-edit__ds-toggle">
												<button
													className={"et-edit__type-btn " + (!f.__dsMode ? "et-edit__type-btn--on" : "")}
													onClick={() => this.onDropdownSourceChange(idx, "manual")}
												>
													<Icon.List size={11} />
													<span>Manual options</span>
												</button>
												<button
													className={"et-edit__type-btn " + (f.__dsMode ? "et-edit__type-btn--on" : "")}
													onClick={() => this.onDropdownSourceChange(idx, "datasource")}
												>
													<Icon.Database size={11} />
													<span>Datasource</span>
												</button>
											</div>
											{f.__dsMode && this.renderDatasourceConfig(f, idx)}
										</div>
									)}

									{f.field_type === "dropdown" && !f.__dsMode && (
										<div className="et-edit__opts">
											<div className="et-edit__opts-title">Options</div>
											{(f.options || []).map((o, optIdx) => (
												<div className="et-edit__opt" key={o.key + "-" + optIdx}>
													<input
														type="color"
														className="et-edit__opt-color"
														value={o.color || "#dfe3eb"}
														onChange={e =>
															this.onChangeOption(idx, optIdx, { color: e.target.value })
														}
														title="Pick color"
													/>
													<input
														className="et-edit__opt-label"
														value={o.label}
														onChange={e =>
															this.onChangeOption(idx, optIdx, { label: e.target.value })
														}
														placeholder="Option label"
													/>
													<button
														className="et-edit__opt-remove"
														onClick={() => this.onRemoveOption(idx, optIdx)}
														title="Remove option"
													>
														<Icon.X size={11} />
													</button>
												</div>
											))}
											<div className="et-edit__opt-actions">
												<button className="et-edit__opt-add" onClick={() => this.onAddOption(idx)}>
													<Icon.Plus size={11} />
													<span>Add option</span>
												</button>
												{(this.props.flowStates || []).length > 0 && (
													<button
														className="et-edit__opt-add et-edit__opt-add--seed"
														onClick={() => this.onLoadEngagementStatuses(idx)}
														title="Import the company's engagement statuses as editable options. They're copies — editing them here never changes the originals."
													>
														<Icon.Download size={11} />
														<span>Load engagement statuses</span>
													</button>
												)}
											</div>
										</div>
									)}
								</div>
							))}
						</div>
					</div>

					<div className="et-modal__footer">
						<button className="et-btn et-btn--ghost" onClick={() => this.props.onClose(false)} disabled={saving}>
							Cancel
						</button>
						<button className="et-btn et-btn--primary" onClick={this.onSaveAll} disabled={saving}>
							{saving ? "Saving…" : "Save changes"}
						</button>
					</div>
				</div>
			</div>
		);
	}
}

export default EditFormModal;
